// src/components/SyncSettings.tsx
import React from 'react';
import { useFirebase } from '@/providers/FirebaseProvider';
import { Button } from '@/components/ui/button';
import { Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { Auth } from '../auth';

interface SyncSettingsProps {
  onDataSync?: () => void;
}

const SyncSettings: React.FC<SyncSettingsProps> = ({ onDataSync }) => {
  const { user, loading: _loading } = useFirebase();
  const [syncStatus, setSyncStatus] = React.useState<'idle' | 'syncing' | 'success' | 'error'>('idle');

  const handleSync = async () => {
    if (!user) return;
    
    setSyncStatus('syncing');
    try {
      // Trigger data sync
      if (onDataSync) {
        onDataSync();
      }
      setSyncStatus('success');
      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch (error) {
      console.error('Error syncing data:', error);
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 2000);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium">Sync & Backup</h3>
        <p className="text-sm text-muted-foreground">
          Sign in to sync your data across devices and keep it backed up.
        </p>
      </div>

      {user ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-md">
            <Cloud className="h-5 w-5 text-primary" />
            <span className="text-sm">Your data is being synced to the cloud</span>
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncStatus === 'syncing'}
              className="flex items-center gap-1"
            >
              <RefreshCw className={`h-4 w-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              {syncStatus === 'syncing' ? 'Syncing...' :
               syncStatus === 'success' ? 'Sync complete!' :
               syncStatus === 'error' ? 'Sync failed' : 'Sync now'}
            </Button>

            <Auth />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <CloudOff className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm">Currently using local storage only</span>
          </div>

          <div className="text-sm text-muted-foreground mb-2">
            Sign in with Google to enable cloud backup and sync across devices.
          </div>

          <Auth onLoginSuccess={handleSync} />
        </div>
      )}
    </div>
  );
};

export default SyncSettings;
